/**
 * In-app notifications — pure helpers.
 *
 * Shared by the super-admin composer (`/admin/communication-center?tab=in-app`)
 * and by the user-facing renderers (bell dropdown, /home/notifications, detail
 * dialog), so the two can never disagree about what a payload means.
 *
 * Kept free of React so the parsing/gating rules are unit-testable without
 * mounting anything.
 *
 * The wire shape is snake_case because it is the `data` blob persisted on the
 * notification row by the backend (`buildInAppNotificationData` in
 * `backend/src/domain/constants/inAppNotification.js`) — NOT a camelCase API
 * response. `parseInAppAnnouncement` is the single place that translation
 * happens; nothing downstream should read raw `data.*` keys.
 */

/** `notifications.type` for an admin-authored in-app announcement. */
export const IN_APP_NOTIFICATION_TYPE = 'IN_APP_ANNOUNCEMENT';

/**
 * `data.kind` discriminator. Renderers key on this rather than on `type`,
 * matching the existing `board_auto_send_failure` / `contact_export_result`
 * convention — a row keeps rendering correctly even if the type string is
 * widened later.
 */
export const IN_APP_DATA_KIND = 'in_app_announcement';

/**
 * The surface this client IS.
 *
 * The web app renders an announcement only when 'web' is among its addressed
 * device types. The audience was already filtered server-side, so this matters
 * for exactly one case — and it is a common one: a user with both the mobile app
 * and the browser open, addressed at Android only. Without this gate the web tab
 * would show a notification the admin deliberately scoped to phones.
 */
export const CURRENT_SURFACE = 'web';

export type InAppMediaType = 'image' | 'video' | 'audio' | 'document';

/**
 * Severity / intent. PRESENTATION ONLY — see the backend constant of the same
 * name. It picks the toast variant, the icon and the accent colour; it never
 * gates delivery, which is why an unrecognised value degrades to `info` rather
 * than causing the announcement to be dropped.
 */
export type InAppSeverity = 'info' | 'success' | 'warning' | 'critical';

export const IN_APP_SEVERITIES: InAppSeverity[] = ['info', 'success', 'warning', 'critical'];

/** Assumed when none was authored — including every row written before the field existed. */
export const DEFAULT_IN_APP_SEVERITY: InAppSeverity = 'info';

/**
 * WHERE an announcement is presented to a user who is in the app.
 *
 *   toast  — a pill in the bottom-right corner, on every page.
 *   banner — a sticky bar across the top of the Overview landing page.
 *   bell   — nothing pops; the bell row is the only surface.
 *   all    — the pill AND the banner.
 *
 * 🔴 The bell row is written in EVERY mode. This chooses what interrupts the
 * user, never whether the announcement is delivered.
 */
export type InAppDisplayMode = 'toast' | 'banner' | 'bell' | 'all';

export const IN_APP_DISPLAY_MODES: InAppDisplayMode[] = ['toast', 'banner', 'bell', 'all'];

/**
 * Assumed when none was authored. `toast` is what this channel already did, so a
 * row written before the field existed keeps popping exactly as it used to.
 */
export const DEFAULT_IN_APP_DISPLAY_MODE: InAppDisplayMode = 'toast';

/** Resolve a placement, degrading anything unrecognised to `toast` (never `all`). */
export function resolveInAppDisplayMode(value: unknown): InAppDisplayMode {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (IN_APP_DISPLAY_MODES as string[]).includes(raw)
    ? (raw as InAppDisplayMode)
    : DEFAULT_IN_APP_DISPLAY_MODE;
}

/** How often the opt-in modal may re-open for one announcement. */
export type InAppModalFrequency = 'once_per_session' | 'every_reload' | 'once_ever';

export const IN_APP_MODAL_FREQUENCIES: InAppModalFrequency[] = [
  'once_per_session',
  'every_reload',
  'once_ever',
];

/**
 * Assumed when none was authored. Deliberately the QUIETEST-but-one option:
 * guessing wrong toward `every_reload` would re-open a modal on every page load
 * for every recipient, which is the failure nobody can undo from their side.
 */
export const DEFAULT_IN_APP_MODAL_FREQUENCY: InAppModalFrequency = 'once_per_session';

/** Mirrors MIN/MAX_IN_APP_AUTO_DISMISS_SECONDS on the backend. */
export const MIN_IN_APP_AUTO_DISMISS_SECONDS = 1;
export const MAX_IN_APP_AUTO_DISMISS_SECONDS = 300;

/** Resolve a severity, degrading anything unrecognised to `info`. */
export function resolveInAppSeverity(value: unknown): InAppSeverity {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (IN_APP_SEVERITIES as string[]).includes(raw)
    ? (raw as InAppSeverity)
    : DEFAULT_IN_APP_SEVERITY;
}

/** Resolve a modal frequency, degrading anything unrecognised to the default. */
export function resolveInAppModalFrequency(value: unknown): InAppModalFrequency {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (IN_APP_MODAL_FREQUENCIES as string[]).includes(raw)
    ? (raw as InAppModalFrequency)
    : DEFAULT_IN_APP_MODAL_FREQUENCY;
}

/**
 * Resolve the toast auto-close duration in seconds.
 *
 * 🔴 `null` IS A REAL VALUE — "persistent until dismissed", not "unset". The
 * null/empty test runs BEFORE `Number()` because `Number(null)` and `Number('')`
 * are both `0`, so coercing first would turn an ABSENT field into a zero that
 * the clamp then floors up to 1 second — a toast that flashes and vanishes.
 * Mirrors `resolveAutoDismissSeconds` on the backend.
 */
export function resolveInAppAutoDismissSeconds(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(
    Math.max(Math.round(n), MIN_IN_APP_AUTO_DISMISS_SECONDS),
    MAX_IN_APP_AUTO_DISMISS_SECONDS
  );
}

export interface InAppMediaItem {
  type: InAppMediaType;
  url: string;
  caption: string | null;
  fileName: string | null;
  thumbnailUrl: string | null;
}

/** A labelled destination rendered as a button under the announcement body. */
export interface InAppActionButton {
  id: string;
  label: string;
  url: string;
}

export interface InAppAnnouncement {
  announcementId: string | null;
  /** 'markdown' bodies are rendered with react-markdown; 'text' is shown verbatim. */
  bodyFormat: 'markdown' | 'text';
  /** The authored SOURCE. Never HTML — see the backend module header. */
  body: string;
  media: InAppMediaItem[];
  actionUrl: string | null;
  actionLabel: string | null;
  /**
   * Up to two secondary buttons, IN ADDITION to `actionUrl`.
   *
   * Kept separate from the primary action because that one is also what a click
   * on the bell row itself follows, whereas these need an explicit tap.
   */
  actionButtons: InAppActionButton[];
  deviceTypes: string[];
  sentByEmail: string | null;
  sentAt: string | null;
  /** True when a modal is also firing for this send, so the generic toast stands down. */
  suppressToast: boolean;
  /**
   * Advisory expiry, ISO. UNLIKE a push TTL — which the transport enforces by
   * dropping an undelivered message — the bell row is already persisted by the
   * time this is read, so nothing deletes it. Past it, the client stops OFFERING
   * the announcement (no modal, no toast).
   */
  expiresAt: string | null;
  /**
   * Presentation intent. Picks the toast variant, the dialog icon and the accent
   * colour — never who receives the announcement, and never whether it renders.
   */
  severity: InAppSeverity;
  /**
   * Toast auto-close, in seconds. `null` means PERSISTENT until dismissed — a
   * real authored choice, not an unset field.
   */
  autoDismissSeconds: number | null;
  /**
   * How often the opt-in modal may re-open for this announcement. Only consulted
   * when the send actually asked for a modal.
   */
  modalFrequency: InAppModalFrequency;
  /** Where this announcement is presented. See InAppDisplayMode. */
  displayMode: InAppDisplayMode;
  /** `default` | `silent`. A silent send never interrupts — no toast, no modal. */
  soundMode: 'default' | 'silent';
  /**
   * Did this send actually ask for the modal?
   *
   * 🔴 DERIVED, because the payload carries no explicit flag. The backend sets
   * `suppress_toast = popupForOnlineUsers || soundMode === 'silent'`, so
   * `suppressToast && soundMode !== 'silent'` is EXACTLY `popupForOnlineUsers`.
   * If that formula ever changes in
   * `backend/src/domain/constants/inAppNotification.js`, this must change with
   * it — grep for `suppress_toast` in both files together.
   *
   * Needed because the modal can be re-opened from the persisted bell row on a
   * later page load, and a row that never wanted a modal must never grow one.
   */
  modalRequested: boolean;
}

const MEDIA_TYPES: InAppMediaType[] = ['image', 'video', 'audio', 'document'];

/** http(s) only — mirrors the backend's `isSafeImageUrl`. */
function isSafeMediaUrl(url: unknown): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

/**
 * Is this URL safe to navigate to / open from a notification?
 *
 * App-relative path or absolute http(s). Rejects `javascript:`, `data:` and
 * protocol-relative `//host` — the same guard the deep-link handlers and the
 * announcement modal already apply.
 */
export function isSafeInAppActionUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('//')) return false;
  if (trimmed.startsWith('/')) return true;
  return /^https?:\/\//i.test(trimmed);
}

/** True when the action URL points inside this app (client-side navigation). */
export function isInternalInAppActionUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().startsWith('/');
}

function normalizeMediaItem(raw: unknown): InAppMediaItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;

  const url = typeof item.url === 'string' ? item.url.trim() : '';
  if (!isSafeMediaUrl(url)) return null;

  const requested = typeof item.type === 'string' ? item.type.trim().toLowerCase() : '';
  const type = (MEDIA_TYPES as string[]).includes(requested)
    ? (requested as InAppMediaType)
    : 'document';

  const str = (v: unknown): string | null => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s ? s : null;
  };

  const thumbnailUrl = typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl.trim() : '';

  return {
    type,
    url,
    caption: str(item.caption),
    fileName: str(item.fileName),
    thumbnailUrl: isSafeMediaUrl(thumbnailUrl) ? thumbnailUrl : null,
  };
}

/**
 * Coerce a notification's `data` blob into an InAppAnnouncement, or null when the
 * row is not one.
 *
 * Returning null is the signal to fall back to the plain title/message rendering
 * every other notification type uses — so a malformed payload degrades to the
 * existing behaviour instead of blanking the row.
 */
export function parseInAppAnnouncement(data: unknown): InAppAnnouncement | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data as Record<string, unknown>;
  if (raw.kind !== IN_APP_DATA_KIND) return null;

  const body = typeof raw.body === 'string' ? raw.body : '';
  const media = Array.isArray(raw.media)
    ? raw.media.map(normalizeMediaItem).filter((m): m is InAppMediaItem => m !== null)
    : [];

  // An announcement with neither a body nor media has nothing to render richly;
  // let the caller fall back to the flattened `message` preview.
  if (!body.trim() && media.length === 0) return null;

  const actionUrl = isSafeInAppActionUrl(raw.action_url) ? String(raw.action_url).trim() : null;
  const soundMode = raw.sound_mode === 'silent' ? 'silent' : 'default';
  const suppressToast = raw.suppress_toast === true;
  const str = (v: unknown): string | null => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s ? s : null;
  };

  return {
    announcementId: str(raw.announcement_id),
    bodyFormat: raw.body_format === 'text' ? 'text' : 'markdown',
    body,
    media,
    actionUrl,
    actionLabel: str(raw.action_label),
    actionButtons: normalizeActionButtons(raw.action_buttons),
    deviceTypes: Array.isArray(raw.device_types)
      ? raw.device_types.filter((d): d is string => typeof d === 'string')
      : [],
    sentByEmail: str(raw.sent_by_email),
    sentAt: str(raw.sent_at),
    suppressToast,
    soundMode,
    modalRequested: suppressToast && soundMode !== 'silent',
    expiresAt: str(raw.expires_at),
    // All three degrade rather than reject: a row written before these fields
    // existed must keep rendering exactly as it always did, which is what the
    // defaults encode (info / persistent / once-per-session).
    severity: resolveInAppSeverity(raw.severity),
    autoDismissSeconds: resolveInAppAutoDismissSeconds(raw.auto_dismiss_seconds),
    modalFrequency: resolveInAppModalFrequency(raw.modal_frequency),
    displayMode: resolveInAppDisplayMode(raw.display_mode),
  };
}

/**
 * Coerce the stored `action_buttons` list.
 *
 * The URL is re-checked HERE even though the backend already validated it: this
 * payload arrives over a socket and from a persisted row that may predate the
 * current rule, and an unsafe href in a rendered button is a live navigation
 * surface in every user's browser. A button that fails the check is DROPPED
 * rather than rendered inert — a button that does nothing when clicked reads as
 * a broken app.
 */
function normalizeActionButtons(raw: unknown): InAppActionButton[] {
  if (!Array.isArray(raw)) return [];
  const out: InAppActionButton[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const b = item as Record<string, unknown>;
    const label = typeof b.label === 'string' ? b.label.trim() : '';
    const url = typeof b.url === 'string' ? b.url.trim() : '';
    if (!label || !isSafeInAppActionUrl(url)) continue;
    out.push({
      id: typeof b.id === 'string' && b.id ? b.id : `action_${out.length + 1}`,
      label,
      url,
    });
    if (out.length >= 2) break;
  }
  return out;
}

/**
 * Has this announcement passed its advisory expiry?
 *
 * 🔴 An UNPARSEABLE or absent `expiresAt` reads as "never expires". Hiding a real
 * announcement because of a malformed timestamp is a silent data-loss bug the
 * user can never diagnose; showing one slightly too long is cosmetic.
 */
export function isInAppAnnouncementExpired(
  announcement: Pick<InAppAnnouncement, 'expiresAt'> | null | undefined,
  now: number = Date.now()
): boolean {
  const at = announcement?.expiresAt;
  if (!at) return false;
  const ms = new Date(at).getTime();
  if (Number.isNaN(ms)) return false;
  return ms <= now;
}

/**
 * Severity → the app's existing toast variant vocabulary.
 *
 * `critical` maps to `destructive` because that is the only variant this design
 * system has for "stop and read this". It is a PRESENTATION mapping — the
 * announcement is not an error, and nothing downstream may branch on it.
 */
export const SEVERITY_TOAST_VARIANT: Record<
  InAppSeverity,
  'info' | 'success' | 'warning' | 'destructive'
> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  critical: 'destructive',
};

/** Label + Tailwind classes per severity. Shared by the dialog, the modal and the bell rows. */
export const SEVERITY_PRESENTATION: Record<
  InAppSeverity,
  { label: string; chipClass: string; iconWrapClass: string; accentClass: string }
> = {
  info: {
    label: 'Info',
    chipClass: 'bg-blue-50 text-blue-700 border-blue-200',
    iconWrapClass: 'bg-blue-50 text-blue-600',
    accentClass: 'text-blue-600',
  },
  success: {
    label: 'Success',
    chipClass: 'bg-green-50 text-green-700 border-green-200',
    iconWrapClass: 'bg-green-50 text-green-600',
    accentClass: 'text-green-600',
  },
  warning: {
    label: 'Warning',
    chipClass: 'bg-amber-50 text-amber-700 border-amber-200',
    iconWrapClass: 'bg-amber-50 text-amber-600',
    accentClass: 'text-amber-600',
  },
  critical: {
    label: 'Critical',
    chipClass: 'bg-red-50 text-red-700 border-red-200',
    iconWrapClass: 'bg-red-50 text-red-600',
    accentClass: 'text-red-600',
  },
};

/**
 * Toast `duration` in ms for an announcement, for Radix's Toast root.
 *
 * 🔴 Returns `Infinity` for a persistent toast, which is Radix's own documented
 * sentinel — its timer helper short-circuits on `Infinity` and never schedules a
 * close. Returning a very large NUMBER instead would work by accident today and
 * silently break if the timer is ever rewritten; `0`/`undefined` would fall back
 * to the provider default (5s) and quietly discard the admin's choice.
 *
 * A non-announcement notification passes `null` and gets `undefined`, so every
 * existing toast in the app keeps the provider default untouched.
 */
export function inAppToastDurationMs(
  announcement: Pick<InAppAnnouncement, 'autoDismissSeconds'> | null | undefined
): number | undefined {
  if (!announcement) return undefined;
  const seconds = announcement.autoDismissSeconds;
  if (seconds === null || seconds === undefined) return Infinity;
  if (!Number.isFinite(seconds) || seconds <= 0) return Infinity;
  return seconds * 1000;
}

/** What the announcement hosts should actually render for one announcement. */
export interface InAppPlacement {
  /** The bottom-right pill. */
  pill: boolean;
  /** The top sticky banner (Overview only — the host applies that route gate). */
  banner: boolean;
}

/** Nothing interrupts the user. The bell row still exists. */
const NO_PLACEMENT: InAppPlacement = { pill: false, banner: false };

/**
 * Resolve WHERE an announcement is presented, for a user who is in the app.
 *
 * 🔴 THE SINGLE SOURCE OF TRUTH for both hosts. The pill and the banner must
 * never decide independently — two components each applying "…unless silent,
 * unless expired, unless another surface" is exactly how one of them ends up
 * showing an announcement the other correctly suppressed.
 *
 * The three refusals below all outrank the authored placement, and each is a
 * decision the admin already made somewhere else:
 *
 *  1. EXPIRED — past its advisory expiry it is not offered at all.
 *  2. WRONG SURFACE — addressed at other device types (mobile-only).
 *  3. SILENT — `soundMode: 'silent'` means "do not interrupt", so it degrades to
 *     bell-only however the placement was set. Honouring the placement would
 *     contradict the very choice that mode expresses, the same way a silent send
 *     already suppresses the modal even with the popup checkbox ticked.
 */
export function resolveInAppPlacement(
  announcement: InAppAnnouncement | null | undefined,
  now: number = Date.now()
): InAppPlacement {
  if (!announcement) return NO_PLACEMENT;
  if (isInAppAnnouncementExpired(announcement, now)) return NO_PLACEMENT;
  if (!isForCurrentSurface(announcement)) return NO_PLACEMENT;
  if (announcement.soundMode === 'silent') return NO_PLACEMENT;

  switch (announcement.displayMode) {
    case 'bell':
      return NO_PLACEMENT;
    case 'banner':
      return { pill: false, banner: true };
    case 'all':
      return { pill: true, banner: true };
    case 'toast':
    default:
      return { pill: true, banner: false };
  }
}

/**
 * Read an announcement off a whole notification object (bell row or socket
 * payload), checking the type as well as the payload.
 */
export function inAppAnnouncementOf(notification: unknown): InAppAnnouncement | null {
  if (!notification || typeof notification !== 'object') return null;
  const n = notification as Record<string, unknown>;
  if (String(n.type || '').toUpperCase() !== IN_APP_NOTIFICATION_TYPE) return null;
  return parseInAppAnnouncement(n.data);
}

/**
 * Is this announcement addressed at THIS client's surface?
 *
 * An empty/absent device list means "everywhere" — an older row written before
 * the field existed, or a send that selected all surfaces. Refusing to render
 * those would silently hide real announcements, which is a worse failure than
 * showing one on an extra surface.
 */
export function isForCurrentSurface(
  announcement: Pick<InAppAnnouncement, 'deviceTypes'> | null | undefined,
  surface: string = CURRENT_SURFACE
): boolean {
  if (!announcement) return false;
  const types = announcement.deviceTypes;
  if (!Array.isArray(types) || types.length === 0) return true;
  return types.includes(surface);
}

/**
 * Should the generic real-time toast fire for this notification?
 *
 * False when the send is ALSO popping a modal — one announcement must not
 * interrupt the user twice with a toast sitting behind a modal that says the same
 * thing. Every other notification is unaffected: the flag only exists on in-app
 * announcement payloads.
 */
export function shouldSuppressToast(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return (data as Record<string, unknown>).suppress_toast === true;
}

/** Human label for a media kind, used in the composer list and the detail dialog. */
export function mediaTypeLabel(type: InAppMediaType): string {
  switch (type) {
    case 'image':
      return 'Image';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    default:
      return 'File';
  }
}

/**
 * Guess a media kind from a URL's extension, for the composer's "Add media" row.
 *
 * A guess only — the admin can override it, and an unrecognised extension becomes
 * `document`, which renders as a download link and is correct for anything.
 */
export function guessMediaType(url: string): InAppMediaType {
  const clean = String(url).split(/[?#]/)[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|avif|bmp|svg)$/.test(clean)) return 'image';
  if (/\.(mp4|webm|mov|m4v|3gp)$/.test(clean)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|aac|amr)$/.test(clean)) return 'audio';
  return 'document';
}

/**
 * Summary suffix for the composer's "sent" toast.
 *
 * Reports the LIVE socket deliveries separately from the persisted rows, because
 * the two numbers answer different questions: how many people were told right
 * now, versus how many will find it in their bell later. Returns '' when the
 * metadata carries neither.
 */
export function inAppDeliverySummary(metadata: unknown): string {
  if (!metadata || typeof metadata !== 'object') return '';
  const live = Number((metadata as Record<string, unknown>).liveSocketDeliveries);
  if (!Number.isFinite(live) || live <= 0) return '';
  return ` · ${live} user${live === 1 ? '' : 's'} notified live`;
}
